import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Grid, Button, useMediaQuery } from "@mui/material";
import PropTypes from "prop-types";
import moment from "moment";

import BaseTable from "Components/Common/BaseTable";
import BreadCrumb from "Components/Common/BreadCrumb";
import SelectField from "Components/Common/SelectField";
import Backdrop from "Components/Common/Backdrop";
import { SUPER_ADMIN } from "common/constant";
import {
  getOptionLabel,
  monthEnum,
  otherUtilityBillingOptions,
} from "common/enums";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

import { useLazyGetOtherMonthlyConnectionChargeInvoiceReport } from "apis/OtherBillingReport/otherBillingReport";
import { useLazyDownloadExcelQuery } from "slices/api";
import DatePicker from "Components/Common/DatePicker";

const MonthlyConnectionChargeInvoiceReport = ({ t }) => {
  const { utilityRoles } = useSelector((state) => state.Login.user);
  const { roles } = useSelector((state) => state.Login);

  const [downloadExcel, { isLoading: isExcelFileDownloading }] =
    useLazyDownloadExcelQuery();

  const [
    getOtherBillingReports,
    {
      data: { data: billingReportsData } = {},
      isLoading: isBillingReportLoading,
      isFetching: isBillingReportFetching,
    },
  ] = useLazyGetOtherMonthlyConnectionChargeInvoiceReport();

  const sm = useMediaQuery("(max-width: 599px)");

  const [filters, setFilters] = useState({
    utilityId: null,
    selectedYear: moment(),
    selectedMonth: monthEnum[0],
  });

  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);

  const resetPageRef = useRef();
  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const otherUtilityFilteredOptions = useMemo(() => {
    const utilityRoleIds = utilityRoles.map(({ utilityId }) => utilityId);

    const filtered = roles.includes(SUPER_ADMIN)
      ? otherUtilityBillingOptions
      : otherUtilityBillingOptions.filter(({ value }) =>
          utilityRoleIds.includes(value)
        );

    return filtered;
  }, [utilityRoles, roles]);

  const buildQuery = () => {
    const query = {
      year: filters.selectedYear?.format("YYYY") || null,
      month: filters.selectedMonth?.value || null,
    };

    if (filters.utilityId) {
      query.utility = filters.utilityId;
    }
    return query;
  };

  const columns = useMemo(
    () => [
      {
        title: "Consumer Number",
        field: "consumerNumber",
      },
      {
        title: "Invoice Number",
        field: "invoiceNumber",
      },
      {
        title: "Consumer Name",
        field: "consumerName",
      },
      {
        title: "Premise Area",
        field: "premiseArea",
      },
      {
        title: "Taxable Amount",
        field: "taxableAmount",
      },
      {
        title: "GST Amount",
        field: "gstAmount",
      },
      {
        title: "Total Amount With GST",
        field: "totalBillAmountWithGST",
      },
      {
        title: "Net Payable",
        field: "netPayable",
      },

      {
        title: "Invoice Date",
        renderCell: ({ invoiceDate }) =>
          moment(invoiceDate).isValid()
            ? convertUTCDateToLocalDate(invoiceDate).format("DD-MM-YYYY")
            : "N/A",
      },
    ],
    []
  );

  const downloadBtnHandler = async () => {
    try {
      const query = buildQuery();

      if (filters.utilityId) {
        const utility = getOptionLabel(
          "otherUtilityApplicationOptions",
          filters.utilityId
        );

        await downloadExcel({
          endpoint:
            "/report/other-utility-monthly-connection-charge-invoice-report-excel",
          sheetName: `${utility} Monthly Connection Charge E-Invoice`,
          query,
        });
      }
    } catch (error) {}
  };

  const getOtherUtilityReportsData = async (query) => {
    try {
      await getOtherBillingReports(query);
    } catch (error) {}
  };

  const handleSearch = () => {
    const query = buildQuery();
    getOtherUtilityReportsData(query);
    handleResetPage();
  };

  const handleClear = () => {
    const newFilters = {
      utilityId: otherUtilityFilteredOptions?.[0]?.value ?? null,
      selectedYear: moment(),
      selectedMonth: monthEnum[0],
    };
    setFilters(newFilters);
    handleResetPage();
  };

  useEffect(() => {
    if (otherUtilityFilteredOptions.length) {
      const firstUtility = otherUtilityFilteredOptions[0]?.value;

      setFilters((prev) => ({
        ...prev,
        utilityId: firstUtility,
      }));

      const query = {
        year: filters.selectedYear?.format("YYYY") || null,
        month: filters.selectedMonth?.value || null,
        utility: firstUtility,
      };

      getOtherUtilityReportsData(query);
    }
  }, [otherUtilityFilteredOptions]);

  return (
    <div className="pb-2">
      <Backdrop
        open={
          isBillingReportFetching ||
          isBillingReportLoading ||
          isExcelFileDownloading
        }
      />
      <BreadCrumb
        title={t("Monthly Connection Charge E-Invoice Report")}
        pageTitle={t("Billing")}
      />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSearch();
        }}
      >
        <Grid container columnSpacing={2} className="mb-1 mt-2">
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SelectField
              label={t("Utility")}
              onChange={(_, { value }) => {
                setFilters((prev) => ({
                  ...prev,
                  utilityId: value,
                }));
              }}
              options={otherUtilityFilteredOptions}
              value={
                otherUtilityFilteredOptions.find(
                  ({ value }) => value === filters.utilityId
                ) ?? ""
              }
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={3}>
            <DatePicker
              slotProps={{
                textField: {
                  size: "small",
                },
              }}
              label={t("Select Year") + "*"}
              className="w-100"
              views={["year"]}
              value={filters.selectedYear}
              disableFuture
              onChange={(date) => {
                setFilters((prevState) => ({
                  ...prevState,
                  selectedYear: date,
                }));
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SelectField
              label={t("Select Month") + "*"}
              options={monthEnum}
              getOptionLabel={({ label }) => label}
              getOptionValue={({ value }) => value}
              onChange={(_, option) => {
                setFilters((prev) => ({
                  ...prev,
                  selectedMonth: option,
                }));
              }}
              value={filters.selectedMonth}
              placeholder="Select Month"
            />
          </Grid>

          <Grid item>
            <Button variant="outlined" className=" me-2" type="submit">
              {t("Search")}
            </Button>
            <Button variant="outlined" onClick={handleClear} className=" me-2">
              {t("Clear")}
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={downloadBtnHandler}
              disabled={!billingReportsData?.length}
            >
              {t("Download")}
            </Button>
          </Grid>
        </Grid>
      </form>

      <BaseTable
        data={billingReportsData || []}
        columns={columns}
        registerResetPage={(cb) => {
          resetPageRef.current = cb;
          setIsResetPageRegistered((prev) => !prev);
        }}
        pageSize={10}
        maxHeight={sm ? "calc(100vh - 330px)" : "calc(100vh - 300px)"}
      />
    </div>
  );
};

MonthlyConnectionChargeInvoiceReport.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(MonthlyConnectionChargeInvoiceReport);
